import { selectUserRoles } from '@iconik-io/shared-lib/src/redux/auth/selectors';

import {
  FEATURE_AI_METADATA_ENRICHMENT,
  FEATURE_AI_METADATA_ENRICHMENT_AUTOMATION,
  FEATURE_CUTTING_ROOM,
  FEATURE_DRM_FORENSIC_WATERMARK,
  FEATURE_EDGE_TRANSCODER,
  FEATURE_ICONIK_SHIELD,
  FEATURE_ISG_GATEWAYS_TAB,
  FEATURE_ISG_PRO,
  FEATURE_MULTI_CHANNEL_AUDIO,
  FEATURE_NATURAL_LANGUAGE_TO_FILTERS,
  FEATURE_NATURAL_SEARCH,
  FEATURE_NATURAL_SEARCH_SEGMENTS,
  FEATURE_NEW_TRANSFER_API,
  FEATURE_PDF_CLICKABLE_LINKS,
  FEATURE_PLAYING_ORIGINAL_DURING_TRANSCODING,
  FEATURE_PORTALS,
  FEATURE_REMOTE_PROXIES,
  FEATURE_SEARCH_BACKENDS,
  FEATURE_UNIFIED_PLAYER_PDF_OPT_OUT,
  FEATURE_WILDMOKA_REPLACES_FFMPEG,
  FEATURE_WILDMOKA_TRANSCODER,
  FEATURE_WM_POSTS_DRAFTS,
  FEATURE_WM_PUBLISH_PANEL,
  FEATURE_WM_PUBLISH_SUPPORTS_TRANSFER_AND_PUBLISH,
} from '../../constants/feature_toggles';
import type { AppState } from '../../store/types';
import { selectIsProOrEnterpriseTier } from '../admin_billing/selectors';
import { selectAiState } from '../ai_settings/selectors';
import { selectCuttingRoomBaseUrl } from '../cutting_room/base_url';

const UNIFIED_PLAYER_PDF_OPT_OUT_KEY = '_unified_player_pdf_opt_out';
const WILDMOKA_TRANSCODER_KEY = '_wildmoka_transcoder';
const WILDMOKA_REPLACES_FFMPEG_KEY = '_wildmoka_replaces_ffmpeg';
const PLAYING_ORIGINAL_DURING_TRANSCODING_KEY =
  '_playing_original_during_transcoding';
const FEATURE_MULTI_CHANNEL_AUDIO_KEY = '_multi_channel_audio';
export const FEATURE_NEW_TRANSFER_API_KEY = '_new_transfer_api';
const FEATURE_WM_PUBLISH_PANEL_KEY = '_wm_publish_panel';
const FEATURE_ISG_PRO_KEY = '_isg_pro';
const FEATURE_ISG_GATEWAYS_TAB_KEY = '_isg_gateways_tab';
const FEATURE_EDGE_TRANSCODER_KEY = 'EDGE_TRANSCODER';
const FEATURE_REMOTE_PROXIES_KEY = 'REMOTE_PROXIES';
const FEATURE_ICONIK_SHIELD_KEY = 'ICONIK_SHIELD';
const FEATURE_SEARCH_BACKENDS_KEY = 'SEARCH_BACKENDS';
const FEATURE_NATURAL_LANGUAGE_TO_FILTERS_KEY = 'NATURAL_LANGUAGE_TO_FILTERS';
const FEATURE_NATURAL_SEARCH_KEY = 'NATURAL_SEARCH';
const FEATURE_NATURAL_SEARCH_SEGMENTS_KEY = 'NATURAL_SEARCH_SEGMENTS';
const FEATURE_AI_METADATA_ENRICHMENT_AUTOMATION_KEY =
  'AI_METADATA_ENRICHMENT_AUTOMATION';
const FEATURE_CUTTING_ROOM_KEY = 'CUTTING_ROOM';
const FEATURE_DRM_FORENSIC_WATERMARK_KEY = 'DRM_FORENSIC_WATERMARK';
const FEATURE_PORTALS_KEY = 'PORTALS';
const FEATURE_PDF_CLICKABLE_LINKS_KEY = 'PDF_CLICKABLE_LINKS';
const FEATURE_WM_PUBLISH_SUPPORTS_TRANSFER_AND_PUBLISH_KEY =
  'WM_PUBLISH_SUPPORTS_TRANSFER_AND_PUBLISH';
const FEATURE_WM_POSTS_DRAFTS_KEY = 'WM_POSTS_DRAFTS';

const getIsFeatureEnabled = (
  appState: AppState,
  featureKey: string,
  localStorageOverride: string,
) => {
  const override = localStorage.getItem(localStorageOverride);
  if (override !== null) {
    return override === 'true' || override === '1';
  }
  return appState.settings.merged.features?.includes(featureKey) ?? false;
};

export const getIsWildmokaTranscoderEnabled = (appState: AppState) =>
  getIsFeatureEnabled(
    appState,
    FEATURE_WILDMOKA_TRANSCODER,
    WILDMOKA_TRANSCODER_KEY,
  );

export const getIsWildmokaReplacesFfmpegEnabled = (appState: AppState) =>
  getIsFeatureEnabled(
    appState,
    FEATURE_WILDMOKA_REPLACES_FFMPEG,
    WILDMOKA_REPLACES_FFMPEG_KEY,
  );

export const getIsPlayingOriginalDuringTranscodingEnabled = (
  appState: AppState,
) =>
  getIsFeatureEnabled(
    appState,
    FEATURE_PLAYING_ORIGINAL_DURING_TRANSCODING,
    PLAYING_ORIGINAL_DURING_TRANSCODING_KEY,
  );

export const getIsUnifiedPlayerPdfEnabled = (appState: AppState) =>
  !getIsFeatureEnabled(
    appState,
    FEATURE_UNIFIED_PLAYER_PDF_OPT_OUT,
    UNIFIED_PLAYER_PDF_OPT_OUT_KEY,
  );

export const getIsMultiChannelAudioEnabled = (appState: AppState) => {
  const isProOrEnterprise = selectIsProOrEnterpriseTier(appState);
  const isMultiChannelFeatureEnabled = getIsFeatureEnabled(
    appState,
    FEATURE_MULTI_CHANNEL_AUDIO,
    FEATURE_MULTI_CHANNEL_AUDIO_KEY,
  );

  return isMultiChannelFeatureEnabled || isProOrEnterprise;
};

export const getIsEdgeTranscoderEnabled = (appState: AppState) =>
  getIsFeatureEnabled(
    appState,
    FEATURE_EDGE_TRANSCODER,
    FEATURE_EDGE_TRANSCODER_KEY,
  );

export const getIsRemoteProxiesEnabled = (appState: AppState) =>
  getIsFeatureEnabled(
    appState,
    FEATURE_REMOTE_PROXIES,
    FEATURE_REMOTE_PROXIES_KEY,
  );

export const getIsIconikShieldFeatureEnabled = (appState: AppState) =>
  getIsFeatureEnabled(
    appState,
    FEATURE_ICONIK_SHIELD,
    FEATURE_ICONIK_SHIELD_KEY,
  );

export const getIsNewTransferApiEnabled = (appState: AppState) =>
  getIsFeatureEnabled(
    appState,
    FEATURE_NEW_TRANSFER_API,
    FEATURE_NEW_TRANSFER_API_KEY,
  );

export const getIsWmPublishPanelEnabled = (appState: AppState) =>
  getIsFeatureEnabled(
    appState,
    FEATURE_WM_PUBLISH_PANEL,
    FEATURE_WM_PUBLISH_PANEL_KEY,
  );

export const getIsWmPostsDraftsEnabled = (appState: AppState) =>
  getIsFeatureEnabled(
    appState,
    FEATURE_WM_POSTS_DRAFTS,
    FEATURE_WM_POSTS_DRAFTS_KEY,
  );

export const getIsIsgProFeatureFlagEnabled = (appState: AppState) =>
  getIsFeatureEnabled(appState, FEATURE_ISG_PRO, FEATURE_ISG_PRO_KEY);

export const getIsIsgProEnabled = (appState: AppState) =>
  getIsIsgProFeatureFlagEnabled(appState) ||
  selectIsProOrEnterpriseTier(appState);

export const getIsIsgGatewaysTabEnabled = (appState: AppState) =>
  getIsFeatureEnabled(
    appState,
    FEATURE_ISG_GATEWAYS_TAB,
    FEATURE_ISG_GATEWAYS_TAB_KEY,
  );

export const getIsIsgAdminPageEnabled = getIsIsgProEnabled;

export const getIsSearchBackendsEnabled = (appState: AppState) =>
  getIsFeatureEnabled(
    appState,
    FEATURE_SEARCH_BACKENDS,
    FEATURE_SEARCH_BACKENDS_KEY,
  );

export const getIsNaturalLanguageToFiltersEnabled = (appState: AppState) =>
  getIsFeatureEnabled(
    appState,
    FEATURE_NATURAL_LANGUAGE_TO_FILTERS,
    FEATURE_NATURAL_LANGUAGE_TO_FILTERS_KEY,
  );

export const getIsNltfAvailable = (appState: AppState) =>
  getIsNaturalLanguageToFiltersEnabled(appState) &&
  Boolean(appState.settings.merged.enable_nltf) &&
  Boolean(selectUserRoles(appState).can_search_with_ai);

/**
 * Whether AI search runs against the natural-search endpoint, where the backend
 * performs the query and returns ranked results, rather than the v1 flow that
 * parses the query into filters client-side and re-runs the UI search.
 *
 * Rides on top of availability rather than widening it: this decides which flow
 * runs once AI search is available to the user, so a domain can be moved over
 * (or rolled back) without AI search itself going away.
 */
export const getIsNaturalSearchAvailable = (appState: AppState) =>
  getIsNltfAvailable(appState) &&
  getIsFeatureEnabled(
    appState,
    FEATURE_NATURAL_SEARCH,
    FEATURE_NATURAL_SEARCH_KEY,
  );

/**
 * Whether the heatmap of matched moments is painted. Rides on top of natural
 * search rather than widening it, so the heatmap can be hidden while AI search
 * itself keeps running.
 */
export const getIsNaturalSearchSegmentsEnabled = (appState: AppState) =>
  getIsNaturalSearchAvailable(appState) &&
  getIsFeatureEnabled(
    appState,
    FEATURE_NATURAL_SEARCH_SEGMENTS,
    FEATURE_NATURAL_SEARCH_SEGMENTS_KEY,
  );

/**
 * The BE masks `enable_ai_metadata_filling` in merged settings to its effective
 * value, which already accounts for the domain feature flag, the AI master
 * switch and the accepted terms.
 */
export const getIsAiMetadataEnrichmentEnabled = (appState: AppState) =>
  Boolean(appState.settings.merged.enable_ai_metadata_filling);

/**
 * Whether the domain is entitled to AI Metadata Enrichment, regardless of it
 * being switched on, e.g. to keep reporting its usage once turned off.
 */
export const getIsAiMetadataEnrichmentAvailable = (appState: AppState) => {
  const status =
    selectAiState(appState)?.features?.[FEATURE_AI_METADATA_ENRICHMENT]?.status;
  return Boolean(status) && status !== 'UNAVAILABLE';
};

export const getIsAiMetadataEnrichmentAutomationEnabled = (
  appState: AppState,
) =>
  getIsAiMetadataEnrichmentEnabled(appState) &&
  getIsFeatureEnabled(
    appState,
    FEATURE_AI_METADATA_ENRICHMENT_AUTOMATION,
    FEATURE_AI_METADATA_ENRICHMENT_AUTOMATION_KEY,
  );

export const getIsCuttingRoomEnabled = (appState: AppState) =>
  getIsFeatureEnabled(appState, FEATURE_CUTTING_ROOM, FEATURE_CUTTING_ROOM_KEY);

export const getIsCuttingRoomConfigured = (appState: AppState) =>
  getIsCuttingRoomEnabled(appState) &&
  Boolean(selectCuttingRoomBaseUrl(appState));

/**
 * Someone who can edit a project can also watch it, so the write role grants
 * read as well. A missing CuttingRoom URL still counts as readable, because the
 * asset view renders its own message for that.
 */
export const getIsCuttingRoomReadable = (appState: AppState) => {
  const userRoles = selectUserRoles(appState);
  return (
    getIsCuttingRoomEnabled(appState) &&
    Boolean(userRoles.can_read_cuttingroom || userRoles.can_write_cuttingroom)
  );
};

/** Also requires a configured URL, since every write flow fails without one. */
export const getIsCuttingRoomWritable = (appState: AppState) =>
  getIsCuttingRoomConfigured(appState) &&
  Boolean(selectUserRoles(appState).can_write_cuttingroom);

export const getIsDrmForensicWatermarkEnabled = (appState: AppState) =>
  getIsFeatureEnabled(
    appState,
    FEATURE_DRM_FORENSIC_WATERMARK,
    FEATURE_DRM_FORENSIC_WATERMARK_KEY,
  );

export const getIsPortalsEnabled = (appState: AppState) =>
  Boolean(appState.settings.merged.enable_portals);

const getIsPortalsFeatureFlagEnabled = (appState: AppState) =>
  getIsFeatureEnabled(appState, FEATURE_PORTALS, FEATURE_PORTALS_KEY);

// The PORTALS flag lets a Starter (PAYGO) domain trial Portals.
export const getCanConfigurePortals = (appState: AppState) =>
  getIsPortalsFeatureFlagEnabled(appState) ||
  selectIsProOrEnterpriseTier(appState);

export const getCanAccessPortals = (appState: AppState) =>
  getIsPortalsEnabled(appState) && getCanConfigurePortals(appState);

export const getIsPdfClickableLinksEnabled = (appState: AppState) =>
  getIsFeatureEnabled(
    appState,
    FEATURE_PDF_CLICKABLE_LINKS,
    FEATURE_PDF_CLICKABLE_LINKS_KEY,
  );

export const getIsWmPublishSupportsTransferAndPublishEnabled = (
  appState: AppState,
) =>
  getIsFeatureEnabled(
    appState,
    FEATURE_WM_PUBLISH_SUPPORTS_TRANSFER_AND_PUBLISH,
    FEATURE_WM_PUBLISH_SUPPORTS_TRANSFER_AND_PUBLISH_KEY,
  );
